import {
  DEFAULT_BLOB_COLORS,
  DEFAULT_VOICE_BLOB_STYLE,
  type BlobColorsByStyle,
  type StudioVoiceBranding,
  type VoiceBlobStyle,
} from '@brandclub/bc-widgets';
import { useVoiceThemeContext } from './VoiceThemeProvider';

export interface ResolvedVoicePersona {
  assistantName: string;
  voiceId: NonNullable<NonNullable<StudioVoiceBranding['persona']>['voiceId']> | undefined;
  /** Visual preset for the on-stage blob. Always resolved (defaults to aurora). */
  blobStyle: VoiceBlobStyle;
  /** Resolved per-blob color slice matching the active `blobStyle`. The full
   *  6-style map is also exposed so previews can swap styles without
   *  re-querying. Defaults to `DEFAULT_BLOB_COLORS` per slot when the brand
   *  has not customized. */
  blobColors: BlobColorsByStyle[VoiceBlobStyle];
  blobColorsAll: BlobColorsByStyle;
}

/**
 * Returns the resolved voice persona for the current brand, with safe defaults
 * so callers never see `undefined` for `assistantName`, `blobStyle`, or any
 * blob color slot.
 *
 * D-61: persona collapsed to `voiceId` + `assistantName` (assistantName is
 * sourced from the unified Identity tab — `personality.name` — by the
 * `useStudioVoiceBranding` hook). `speakingStyle` and `greetingTemplate`
 * were dropped from the schema; voice instructions still pull a tone hint
 * from `personality.tone` server-side.
 */
export function useVoicePersona(): ResolvedVoicePersona {
  const { persona, visual } = useVoiceThemeContext();
  const blobStyle = visual?.blobStyle ?? DEFAULT_VOICE_BLOB_STYLE;

  const blobColorsAll: BlobColorsByStyle = {
    aurora: { ...DEFAULT_BLOB_COLORS.aurora, ...visual?.blobColors?.aurora },
    organic: { ...DEFAULT_BLOB_COLORS.organic, ...visual?.blobColors?.organic },
    particle: { ...DEFAULT_BLOB_COLORS.particle, ...visual?.blobColors?.particle },
    waveform: { ...DEFAULT_BLOB_COLORS.waveform, ...visual?.blobColors?.waveform },
    ripple: { ...DEFAULT_BLOB_COLORS.ripple, ...visual?.blobColors?.ripple },
    metaball: { ...DEFAULT_BLOB_COLORS.metaball, ...visual?.blobColors?.metaball },
  };

  return {
    assistantName: persona.assistantName || 'Benny',
    voiceId: persona.voiceId,
    blobStyle,
    blobColors: blobColorsAll[blobStyle],
    blobColorsAll,
  };
}

export default useVoicePersona;
